import Link from "next/link";
import { useRouter } from "next/router";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { twMerge } from "tailwind-merge";

import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import useWindowSize from "hooks/useWindowSize";

import styles from "./style.module.scss";

export type PermissionKey =
  | "canViewQuiddityTools"
  | "canViewPaymentAnalytics"
  | "isIntroducer";

type TabType =
  | "explore"
  | "markets"
  | "event-driven"
  | "screeners"
  | "insight-providers"
  | "channel-partner"
  | "charting";

export type Tab = {
  id: TabType;
  segments?: { id: TabType; name: string; permission?: PermissionKey }[];
  name: string;
  href: string;
  permission?: PermissionKey;
};

export const AVAILABLE_TABS: ("separator" | Tab)[] = [
  {
    id: "explore",
    segments: [
      { id: "screeners", name: "Screeners & Comparisons" },
      {
        id: "event-driven",
        name: "Event-Driven",
        permission: "canViewQuiddityTools",
      },
      { id: "charting", name: "Charting" },
      { id: "markets", name: "Markets" },
    ],
    name: "Explore",
    href: "/tools?tab=explore",
  },
  {
    id: "insight-providers",
    name: "For Insight Providers",
    href: "/tools?tab=insight-providers",
    permission: "canViewPaymentAnalytics",
  },
  {
    id: "channel-partner",
    name: "For Channel Partners",
    href: "/tools?tab=channel-partner",
    permission: "isIntroducer",
  },
];

type ToolsPageTabsProps = {};

const ToolsPageTabs = ({}: ToolsPageTabsProps) => {
  const router = useRouter();
  const tabQuery = router.query.tab as string;

  const { account } = useAuthentication();
  const { mobile } = useMediaQuery();

  const tabs = useMemo(() => {
    return AVAILABLE_TABS.filter(
      (t) => t === "separator" || !t.permission || account?.[t.permission]
    );
  }, [account]);

  const scrollableRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(false);
  const scroll = useCallback(
    (direction) => {
      const scrollable = scrollableRef.current;
      const scrollLeft = scrollable?.scrollLeft || 0;
      const offset = (direction === "right" ? 1 : -1) * Math.round(width / 2);
      scrollable?.scroll({ left: scrollLeft + offset, behavior: "smooth" });
    },
    [width]
  );

  const scrollToTab = useCallback((tab) => {
    const scrollable = scrollableRef.current;

    const scrollLeft =
      tab.offsetLeft -
      (scrollable?.offsetLeft || 0) +
      tab.offsetWidth -
      (scrollable?.scrollWidth || 0) / 2;

    scrollable?.scroll({
      left: scrollLeft,
      behavior: "smooth",
    });
  }, []);

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      try {
        const tab = event.currentTarget;
        scrollToTab(tab);
      } catch {}
    },
    [scrollToTab]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  useEffect(() => {
    const scrollable = scrollableRef.current;

    const updateNavButtons = () => {
      if (scrollable?.scrollLeft === 0) {
        setShowLeftNav(false);
      } else {
        setShowLeftNav(true);
      }
      if (
        (scrollable?.scrollLeft || 0) + width >=
        (scrollable?.scrollWidth || 0)
      ) {
        setShowRightNav(false);
      } else {
        setShowRightNav(true);
      }
    };

    updateNavButtons();

    scrollable?.addEventListener("scroll", updateNavButtons, { passive: true });

    return () => {
      scrollable?.removeEventListener("scroll", updateNavButtons);
    };
  }, [width]);

  const [stickyTop] = useState(false);
  const [top] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        className={twMerge(
          styles.placeholder,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs
        )}
        ref={containerRef}
      />
      <div
        className={twMerge(
          styles.discussionsPageTabs,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs,
          "skiptranslate"
        )}
        style={stickyTop ? { top } : {}}
      >
        {showLeftNav && (
          <button
            title="scroll left"
            className={twMerge(styles.tabsNav, styles.left)}
            onClick={() => scroll("left")}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>
        )}
        <div className={styles.tabs} ref={scrollableRef}>
          {tabs.map((tab) => {
            if (tab === "separator") {
              return <hr key={tab} className="ml-[10px] mr-[25px] mt-0" />;
            }

            return (
              <React.Fragment key={tab.name}>
                <Link
                  href={tab.href}
                  prefetch={false}
                  onClick={onTabClick}
                  className={twMerge(
                    styles.item,
                    tab.id === tabQuery || (!tabQuery && tab.id === "explore")
                      ? styles.activeTab
                      : undefined
                  )}
                  ref={(ref) => onTabLoad(ref, tab)}
                >
                  {tab.name}
                </Link>
              </React.Fragment>
            );
          })}
        </div>
        {showRightNav && (
          <button
            title="scroll right"
            className={twMerge(styles.tabsNav, styles.right)}
            onClick={() => scroll("right")}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        )}
      </div>
    </>
  );
};

export default ToolsPageTabs;
